import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createReportRequest } from "../../../api/inform";
import ReportRequestList, { humanizeReportType } from "../../../components/reportRequestList/ReportRequestList";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../../components/dateRangeFilter/DateRangeFilter";
import useAuth from "../../../hooks/useAuth";
import useReportRequestPolling from "../../../hooks/useReportRequestPolling";
import { getOperationTimezone } from "../../../utils/timezone";
import {
  PackageCheck, ClipboardList, Truck, UserRound, Users,
  Send, AlertCircle, CheckCircle2, Calendar, Clock,
  FileSpreadsheet, FileText, Package,
} from "lucide-react";

const KAM_REPORT_TYPES = [
  {
    value: "general_deliveries",
    label: "Pedidos entregados",
    Icon: PackageCheck,
    description: "Paquetes entregados en el período seleccionado",
    payload: { type: "general", generalCategory: "deliveries" },
  },
  {
    value: "general_all",
    label: "Pedidos creados",
    Icon: ClipboardList,
    description: "Todos los paquetes creados en el período seleccionado",
    payload: { type: "general", generalCategory: "all_packages" },
  },
  {
    value: "fleet_packages",
    label: "Pedidos flota propia",
    Icon: Truck,
    description: "Todos los pedidos gestionados por tu flota propia en el período seleccionado",
    payload: { type: "general", generalCategory: "fleet_packages" },
  },
  {
    value: "user_packages",
    label: "Mis pedidos",
    Icon: UserRound,
    description: "Pedidos creados por mí en el período seleccionado",
    payload: { type: "user_packages" },
  },
  {
    value: "fleet_driver_data",
    label: "Datos de repartidores",
    Icon: Users,
    description: "Snapshot con datos personales y bancarios de los repartidores de tu flota",
    payload: { type: "fleet_driver_data" },
    snapshot: true,
  },
];

// Presets del wizard: el set canónico pero con "Este mes hasta hoy" en lugar de
// "Este mes" (mes completo), para no generar un endDate futuro al exportar.
const REPORT_PRESETS = DEFAULT_PRESETS.map((p) =>
  p.key === "thisMonth" ? { ...p, key: "thisMonthToToday" } : p,
);

const localDateToUtcRange = (dateStr, timezone) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  const end = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMs = getTimezoneOffsetMs(tz, start);
  return {
    start: new Date(start.getTime() + offsetMs),
    end: new Date(end.getTime() + offsetMs),
  };
};

const getTimezoneOffsetMs = (tz, refDate) => {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = fmt.formatToParts(refDate).reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
    const localTime = new Date(
      `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}:${parts.second}Z`
    );
    return refDate.getTime() - localTime.getTime();
  } catch {
    return 0;
  }
};

export default function ClientInform() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userTimezone = getOperationTimezone(user);
  const hasOwnFleet = user?.company?.hasOwnFleet;

  const availableTypes = useMemo(
    () => KAM_REPORT_TYPES.filter((t) => {
      if (t.value === "fleet_driver_data" && !hasOwnFleet) return false;
      if (t.value === "fleet_packages" && !hasOwnFleet) return false;
      return true;
    }),
    [hasOwnFleet],
  );

  const [selectedType, setSelectedType] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [datePreset, setDatePreset] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const { requests, loadingList, refetch } = useReportRequestPolling();

  const handleTypeSelect = (typeValue) => {
    setSelectedType(typeValue);
    setSubmitError(null);
    setSuccessMsg(null);
  };

  const selectedReportType = KAM_REPORT_TYPES.find((rt) => rt.value === selectedType);
  const isSnapshot = selectedReportType?.snapshot;

  const isSubmitDisabled = () => {
    if (!selectedType) return true;
    if (isSnapshot) return false;
    if (!startDate || !endDate) return true;
    if (new Date(endDate) < new Date(startDate)) return true;
    return false;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSuccessMsg(null);

    const reportType = KAM_REPORT_TYPES.find((rt) => rt.value === selectedType);

    let payload;
    if (reportType?.snapshot) {
      payload = { ...reportType.payload };
    } else {
      const startRange = localDateToUtcRange(startDate, userTimezone);
      const endRange = localDateToUtcRange(endDate, userTimezone);

      if (!startRange || !endRange) {
        setSubmitError(t("Fechas inválidas. Verifica el rango seleccionado."));
        setSubmitting(false);
        return;
      }

      payload = {
        ...reportType.payload,
        startDate: startRange.start.toISOString(),
        endDate: endRange.end.toISOString(),
        timezone: userTimezone,
      };
    }

    const result = await createReportRequest(payload);

    if (result?.error) {
      const msg =
        result.error?.message ||
        result.error?.error ||
        t("Error al crear el informe. Intente nuevamente.");
      setSubmitError(msg);
    } else {
      setSuccessMsg(t("Informe solicitado exitosamente. Aparecerá en el listado a continuación."));
      setSelectedType(null);
      setStartDate("");
      setEndDate("");
      setDatePreset("");
      await refetch();
      setTimeout(() => setSuccessMsg(null), 6000);
    }

    setSubmitting(false);
  };

  return (
    <div className="w-100 all-orders-container mt-6 ir-page">
      <div className="ir-header">
        <h2 className="ir-title">{t("Informes")}</h2>
        <p className="ir-subtitle">
          {t("Solicita informes de tus pedidos. Se procesan y podrás descargarlos cuando estén listos.")}
        </p>
      </div>

      <div className="ir-layout">
        <section className="ir-form-card" aria-label={t("Solicitar informe")}>
          <h3 className="ir-section-title">{t("Solicitar informe")}</h3>

          <div className="ir-step">
            <span className="ir-step-label">{t("1. Tipo de informe")}</span>
            <div className="ir-type-grid">
              {availableTypes.map((typeItem) => (
                <button
                  key={typeItem.value}
                  className={`ir-type-btn ${selectedType === typeItem.value ? "ir-type-btn--active" : ""}`}
                  onClick={() => handleTypeSelect(typeItem.value)}
                  type="button"
                >
                  <span className="ir-type-icon"><typeItem.Icon size={20} strokeWidth={1.75} /></span>
                  <span className="ir-type-name">{t(typeItem.label)}</span>
                  <span className="ir-type-desc">{t(typeItem.description)}</span>
                </button>
              ))}
            </div>
          </div>

          {!isSnapshot && (
          <div className="ir-step">
            <span className="ir-step-label">{t("2. Rango de fechas")}</span>
            <DateRangeFilter
              compact
              value={datePreset}
              presets={REPORT_PRESETS}
              onChange={(s, e, p) => {
                setStartDate(s);
                setEndDate(e);
                setDatePreset(p);
              }}
            />
            {startDate && endDate && new Date(endDate) < new Date(startDate) && (
              <p className="ir-validation-error">
                {t("La fecha de fin debe ser mayor o igual a la de inicio.")}
              </p>
            )}
          </div>
          )}

          {submitError && (
            <div className="ir-alert ir-alert--error" role="alert">
              <span className="ir-alert-icon"><AlertCircle size={18} /></span>
              <span>{submitError}</span>
            </div>
          )}

          {successMsg && (
            <div className="ir-alert ir-alert--success" role="alert">
              <span className="ir-alert-icon"><CheckCircle2 size={18} /></span>
              <span>{successMsg}</span>
            </div>
          )}

          <div className="ir-form-actions">
            {/* El botón arranca deshabilitado y no decía qué faltaba: el operador
                hacía clic y no pasaba nada. */}
            {isSubmitDisabled() && !submitting && (
              <p className="text-xs text-fg-muted mb-2">
                {!selectedType
                  ? t("Selecciona un tipo de informe.")
                  : t("Selecciona el rango de fechas para continuar.")}
              </p>
            )}
            <button
              className="ir-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitDisabled() || submitting}
              type="button"
            >
              {submitting ? (
                <>
                  <span className="ir-spinner" aria-hidden="true" />
                  {t("Enviando solicitud...")}
                </>
              ) : (
                <>
                  <Send size={16} aria-hidden="true" />
                  {t("Solicitar informe")}
                </>
              )}
            </button>
          </div>
        </section>

        <ReportRequestList
          requests={requests}
          loading={loadingList}
          onRefresh={refetch}
          resolveTypeLabel={(req) => {
            const typeData = KAM_REPORT_TYPES.find(
              (rt) =>
                rt.payload.type === req.type &&
                (rt.payload.generalCategory === req.generalCategory ||
                  (!rt.payload.generalCategory && !req.generalCategory))
            );
            const IconComp = typeData?.Icon || FileText;
            return {
              label: t(typeData?.label || humanizeReportType(req.type)),
              icon: <IconComp size={15} aria-hidden="true" />,
            };
          }}
          renderMetadata={(req, { formatDateOnly, formatDisplayDate }) => (
            <>
              {req.startDateUtc && (
                <span className="ir-meta-item">
                  <Calendar size={13} aria-hidden="true" />
                  {formatDateOnly(req.startDateUtc, req.timezone)} —{" "}
                  {formatDateOnly(req.endDateUtc, req.timezone)}
                </span>
              )}
              <span className="ir-meta-item">
                <Clock size={13} aria-hidden="true" />
                {t("Solicitado:")} {formatDisplayDate(req.createdAt)}
              </span>
            </>
          )}
          renderRowCount={(req) => {
            if (req.rowCount == null) return null;
            const RowIcon = req.type === "fleet_driver_data" ? Users : Package;
            const unit = req.type === "fleet_driver_data" ? t("repartidores") : t("paquetes");
            return <span className="ir-meta-item ir-meta-rows"><RowIcon size={13} aria-hidden="true" /> {req.rowCount} {unit}</span>;
          }}
          renderDownloadButtons={(req, handleDownload) => (
            <>
              {req.files?.xlsxUrl && (
                <button className="ir-download-btn ir-download-btn--xlsx" onClick={() => handleDownload(req.files.xlsxUrl, `informe_${req._id}.xlsx`)} type="button" title={t("Descargar en formato Excel")}>
                  <FileSpreadsheet size={14} aria-hidden="true" /> Excel
                </button>
              )}
              {req.files?.csvUrl && (
                <button className="ir-download-btn ir-download-btn--csv" onClick={() => handleDownload(req.files.csvUrl, `informe_${req._id}.csv`)} type="button" title={t("Descargar en formato CSV")}>
                  <FileText size={14} aria-hidden="true" /> CSV
                </button>
              )}
              {req.files?.csvDeliveryUrl && (
                <button className="ir-download-btn ir-download-btn--csv" onClick={() => handleDownload(req.files.csvDeliveryUrl, `entregas_${req._id}.csv`)} type="button" title={t("CSV de Entregas")}>
                  <FileText size={14} aria-hidden="true" /> CSV Entregas
                </button>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
}
